import React from "react"
import { createRoot } from "react-dom/client"

// Shared client runtime that replaces react_on_rails. Finds server-rendered mount points emitted by
// ReactHelper (`<div data-react-component="Name" data-react-props="{...}">`) and renders each with
// React.createElement(Component, props). Client-only — no SSR. Used by both the Vite app bundle and
// the (shakapacker-built) Active Admin bundle, each with its own component registry.
// Approximates react_on_rails' railsContext for components that still use the render-function
// signature `(props, railsContext) => element | component`.
function buildRailsContext() {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    href: window.location.href,
    location: window.location.href,
    origin: window.location.origin,
    i18nLocale: document.documentElement.lang || "en",
  }
}

// Resolves a registry entry to a React element. Plain components render directly; render-functions
// (arity >= 2, i.e. they take railsContext) are invoked and their returned element/component unwrapped.
function toElement(Component, props) {
  if (typeof Component === "function" && Component.length >= 2) {
    const rendered = Component(props, buildRailsContext())
    return React.isValidElement(rendered)
      ? rendered
      : React.createElement(rendered)
  }
  return React.createElement(Component, props)
}

export function mountReactComponents(registry) {
  const mountAll = () => {
    document.querySelectorAll("[data-react-component]").forEach((el) => {
      if (el.dataset.reactMounted) return

      const name = el.dataset.reactComponent
      const Component = registry[name]

      if (!Component) {
        // eslint-disable-next-line no-console
        console.error(`[react-mount] Unknown component: ${name}`)
        return
      }

      let props = {}
      try {
        const propsScript = el.querySelector("script.js-react-props")
        props = propsScript ? JSON.parse(propsScript.textContent) : {}
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`[react-mount] Bad props for ${name}`, error)
      }

      createRoot(el).render(toElement(Component, props))
      el.dataset.reactMounted = "true"
    })
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mountAll)
  } else {
    mountAll()
  }
}
